import { type BoxRect, type GeometryResolver } from '../types';

type RowSpan = { y: number; height: number };
type ColSpan = { x: number; width: number };

/**
 * Decorator for `GeometryResolver` that caches physical row and column spans.
 * When cells or rows unmount due to virtualization, this resolver synthesizes
 * phantom bounds from the cache so selection highlights and focus rings do not
 * collapse. Call `clearCache` after layout-changing events (see
 * `HighlightsController.clearVirtualGeometryCache`).
 */
export class VirtualGeometryResolver implements GeometryResolver {
  #baseResolver: GeometryResolver;

  #rowCache = new Map<number, RowSpan>();
  #colCache = new Map<number, ColSpan>();
  // Track the max row natively to make reads O(1)
  #maxCachedRowIndex = -1;

  constructor(baseResolver: GeometryResolver) {
    this.#baseResolver = baseResolver;
  }

  getCellBounds(rowIndex: number, colIndex: number): BoxRect | null {
    const realBounds = this.#baseResolver.getCellBounds(rowIndex, colIndex);

    if (realBounds) {
      this.#rowCache.set(rowIndex, { y: realBounds.y, height: realBounds.height });
      this.#colCache.set(colIndex, { x: realBounds.x, width: realBounds.width });

      // Update our tracker
      this.#maxCachedRowIndex = Math.max(this.#maxCachedRowIndex, rowIndex);

      return realBounds;
    }

    const cachedRow = this.#rowCache.get(rowIndex);
    let cachedCol = this.#colCache.get(colIndex);

    if (!cachedCol) {
      const fallbackColBounds = this.#baseResolver.getColumnBounds(colIndex);

      if (fallbackColBounds) {
        cachedCol = { x: fallbackColBounds.x, width: fallbackColBounds.width };
        this.#colCache.set(colIndex, cachedCol);
      }
    }

    if (cachedRow && cachedCol) {
      return {
        x: cachedCol.x,
        y: cachedRow.y,
        width: cachedCol.width,
        height: cachedRow.height,
      };
    }

    if (cachedCol) {
      return {
        x: cachedCol.x,
        y: this.#clampYForUnknownRow(rowIndex),
        width: cachedCol.width,
        height: 0,
      };
    }

    return null;
  }

  getRowBounds(rowIndex: number): BoxRect | null {
    const realBounds = this.#baseResolver.getRowBounds(rowIndex);

    if (realBounds) {
      this.#rowCache.set(rowIndex, { y: realBounds.y, height: realBounds.height });

      // Update our tracker
      this.#maxCachedRowIndex = Math.max(this.#maxCachedRowIndex, rowIndex);

      return realBounds;
    }

    const cachedRow = this.#rowCache.get(rowIndex);
    const containerBounds = this.getContainerBounds();
    const fallbackX = containerBounds?.x ?? 0;
    const fallbackWidth = containerBounds?.width ?? 0;

    if (cachedRow) {
      return {
        x: fallbackX,
        y: cachedRow.y,
        width: fallbackWidth,
        height: cachedRow.height,
      };
    }

    return {
      x: fallbackX,
      y: this.#clampYForUnknownRow(rowIndex),
      width: fallbackWidth,
      height: 0,
    };
  }

  getColumnBounds(colIndex: number): BoxRect | null {
    const realBounds = this.#baseResolver.getColumnBounds(colIndex);

    if (realBounds) {
      this.#colCache.set(colIndex, { x: realBounds.x, width: realBounds.width });

      return realBounds;
    }

    const cachedCol = this.#colCache.get(colIndex);

    if (cachedCol) {
      const containerBounds = this.getContainerBounds();
      const scrollContainer = this.getScrollContainer();

      return {
        x: cachedCol.x,
        y: containerBounds?.y ?? 0,
        width: cachedCol.width,
        height: scrollContainer?.scrollHeight ?? 0,
      };
    }

    return null;
  }

  /**
   * Unknown row index: `y = 0` unless any rows are cached and `rowIndex` exceeds
   * the largest cached index, then `y = scrollHeight` as a bottom anchor.
   */
  #clampYForUnknownRow(rowIndex: number): number {
    // Use our O(1) tracker instead of generating an array and iterating
    if (this.#rowCache.size > 0 && rowIndex > this.#maxCachedRowIndex) {
      const scrollContainer = this.getScrollContainer();

      return scrollContainer?.scrollHeight ?? 0;
    }

    return 0;
  }

  getContainerBounds(): BoxRect | null {
    return this.#baseResolver.getContainerBounds();
  }

  getScrollContainer(): HTMLElement | null {
    return this.#baseResolver.getScrollContainer();
  }

  clearCache() {
    this.#rowCache.clear();
    this.#colCache.clear();

    // Reset our tracker when the cache empties
    this.#maxCachedRowIndex = -1;
  }
}
